/**
 * Utility functions for managing last viewed events in localStorage
 * Separated from the composable to avoid unnecessary instantiation
 */

const LAST_VIEWED_EVENTS_LOCAL_KEY = 'last-viewed-event-ids'
const LAST_VIEWED_EVENTS_TIME_LOCAL_STORAGE_KEY = 'last-viewed-events-time'

const getLastViewedStorageKey = () => `${LAST_VIEWED_EVENTS_LOCAL_KEY}`

const safeJsonParse = (str: string | null, defaultReturn: any[] = []) => {
  let arr = defaultReturn || []
  if (!str) return arr
  try {
    arr = JSON.parse(str)
  } catch (_err) {
    // eslint-disable-next-line no-console
    console.warn('JSON.parse error', _err)
  }
  return arr
}

/**
 * Stores an event ID in the last viewed events list in localStorage
 * Maintains a maximum of 8 events, with most recent first
 */
export const setLastViewedEvent = (eventId: string): string[] | undefined => {
  if (!process.client || !window?.localStorage || !eventId) {
    return
  }

  const now = Date.now()
  const lastViewedIds = [eventId]

  const storedEventsId = window.localStorage.getItem(getLastViewedStorageKey())

  if (storedEventsId) {
    let newStoredEventsId = safeJsonParse(storedEventsId, [])

    if (newStoredEventsId.includes(eventId)) {
      newStoredEventsId.splice(
        newStoredEventsId.findIndex((v: string) => v === eventId),
        1
      )
    }
    newStoredEventsId.unshift(eventId)
    newStoredEventsId = newStoredEventsId.slice(0, 8)

    window.localStorage.setItem(
      getLastViewedStorageKey(),
      JSON.stringify(newStoredEventsId)
    )

    return newStoredEventsId
  } else {
    window.localStorage.setItem(
      getLastViewedStorageKey(),
      JSON.stringify(lastViewedIds)
    )
    window.localStorage.setItem(
      LAST_VIEWED_EVENTS_TIME_LOCAL_STORAGE_KEY,
      now.toString()
    )

    return lastViewedIds
  }
}

/**
 * Retrieves the list of last viewed event IDs from localStorage
 */
export const getLastViewedEventIds = (): string[] => {
  if (!process.client || !window?.localStorage) {
    return []
  }

  const storedEventsId = window.localStorage.getItem(getLastViewedStorageKey())
  return safeJsonParse(storedEventsId, [])
}

/**
 * Gets the timestamp of when the last viewed events were first stored
 */
export const getLastViewedEventsTime = (): number | null => {
  if (!process.client || !window?.localStorage) {
    return null
  }

  const time = window.localStorage.getItem(
    LAST_VIEWED_EVENTS_TIME_LOCAL_STORAGE_KEY
  )
  return time ? Number(time) : null
}
